import { useFloating } from "@floating-ui/react";
import each from "lodash/each";
import { getChatSendOnEnter } from "PFApp/components/chat/send_on_enter";
import { InputFieldSet } from "PFComponents/text/input_field_set";
import { gidFor } from "PFCore/helpers/gid";
import { getProfileName } from "PFCore/helpers/profile";
import { EventKey } from "PFCore/types/event_key";
import { GIdTargetType } from "PFTypes";
import { ForwardedRef, forwardRef, ReactNode, useImperativeHandle, useRef, useState } from "react";
import { useClickAway } from "react-use";

import DropDown from "../../dropdown/dropdown";
import { TextAreaProps } from "../text_area";
import css from "./chat_input_field_set.module.scss";
import { useAutcompleteData } from "./use_autocomplete_data";
import { useChatInputFieldSet } from "./use_chat_input_field_set";

export type ChatInputFieldSetProps = Omit<TextAreaProps, "value"> & {
  value?: string;
  profileId: number;
  conversationId: number;
  displayEmojis?: boolean;
  displayMentions?: boolean;
  displayAbove?: boolean;
  minVisibleMaxLengthFactor?: number;
  maxLengthLabelClassName?: string;
  controlledValue?: boolean;
  actions?: ReactNode;
  onSubmit: VoidFunction;
};

type DropdownRef = {
  handleKeyUp?: (event: KeyboardEvent) => void;
  handleKeyDown?: (event: KeyboardEvent) => void;
};

export const ChatInputFieldSet = forwardRef(
  (
    {
      profileId,
      conversationId,
      displayAbove,
      displayEmojis = true,
      displayMentions,
      onBlur,
      onChange,
      onClick,
      onKeyUp,
      onSubmit,
      value,
      actions,
      ...props
    }: ChatInputFieldSetProps,
    ref: ForwardedRef<{ toggleEmojIsOpen: () => void }>
  ) => {
    const [selection, setSelection] = useState({ selectionStart: 0, selectionEnd: 0 });

    const inputRef = useRef<HTMLTextAreaElement>(null);
    const dropdownRef = useRef<DropdownRef>({});

    const { autoCompleteDropdowns, mentions, autoCompleteReplace } = useAutcompleteData({
      displayEmojis,
      displayMentions,
      profileId,
      conversationId,
      value
    });

    const { dropdownOptions, closeDropdown, dropdownTrigger, toggleEmojIsOpen, setEmojIsOpen } =
      useChatInputFieldSet({
        autoCompleteDropdowns,
        value,
        selection,
        inputRef
      });

    const updateSelectionFromTarget = (target) => {
      const { selectionStart, selectionEnd } = target;
      setSelection({ selectionStart, selectionEnd });
    };

    const postReplace = (value) => {
      const sortedMentions = Object.keys(mentions).sort((keyA, keyB) => keyB.length - keyA.length);

      sortedMentions.forEach((key) => {
        const mention = mentions[key];
        const gid = gidFor(GIdTargetType.Profile, mention.id);
        const mentionDisplay = getProfileName(mention);

        value = value.split(`@${key}`).join(`@[${mentionDisplay}](${gid})`);
      });

      return value;
    };

    const applyChange = ({ value, selectionStart, selectionEnd }) => {
      let head = value.substr(0, selectionStart);
      let body = value.substr(selectionStart, selectionEnd - selectionStart);
      let tail = value.substr(selectionEnd, value.length - selectionEnd);

      //Replace
      each(autoCompleteReplace, (replaceLogic) => {
        head = head.replace(replaceLogic.pattern, replaceLogic.replace);
        body = body.replace(replaceLogic.pattern, replaceLogic.replace);
        tail = tail.replace(replaceLogic.pattern, replaceLogic.replace);
      });

      const nextValue = head + body + tail;

      setSelection({
        selectionStart: head.length,
        selectionEnd: head.length + body.length
      });

      onChange?.(nextValue, postReplace(nextValue));
    };

    const handleClick = (event) => {
      updateSelectionFromTarget(event.target);

      onClick?.(event);
    };

    const isDropdownOpen = dropdownOptions.length > 0;

    const handleKeyDown = (event) => {
      dropdownRef.current?.handleKeyDown?.(event);
      const sendOnEnter = getChatSendOnEnter();
      const otherMod = event.metaKey || event.ctrlKey;

      if (
        event.key === EventKey.Enter &&
        ((sendOnEnter && !event.shiftKey && !isDropdownOpen) || (!sendOnEnter && otherMod))
      ) {
        event.preventDefault();
        onSubmit();
      }
    };

    const handleKeyUp = (event) => {
      updateSelectionFromTarget(event.target);

      onKeyUp?.(event);
    };

    const handleChange = (value, event) => {
      const { selectionStart, selectionEnd } = event.target;
      setEmojIsOpen(false);
      applyChange({ value, selectionStart, selectionEnd });
    };

    const handleBlur = (event) => {
      closeDropdown();

      onBlur?.(event);
    };

    const handleDropdownClick = (item) => {
      const { selectionStart, selectionEnd } = selection;

      const head = value?.substr(0, selectionStart).replace(dropdownTrigger || "", "") + item();
      const tail = value?.substr(selectionEnd, value.length - selectionEnd);

      const nextValue = head + tail;

      const newSelection = { selectionStart: head.length, selectionEnd: head.length };
      setSelection(newSelection);
      closeDropdown();
      inputRef.current?.focus();

      applyChange({ value: nextValue, ...newSelection });
    };

    useImperativeHandle(ref, () => ({
      toggleEmojIsOpen
    }));

    const containerRef = useRef(null);
    useClickAway(containerRef, handleBlur);

    const { refs, floatingStyles } = useFloating({
      placement: "top-end"
    });

    return (
      <div ref={containerRef} className={css.inputTextInner} style={{ position: "relative" }}>
        <div ref={refs.setReference} className={css.inputInner}>
          <InputFieldSet
            {...props}
            ref={inputRef}
            value={value}
            onChange={handleChange}
            onClick={handleClick}
            onKeyUp={handleKeyUp}
            onKeyDown={handleKeyDown}
            maxHeight={80}
            inputType="textarea"
          />
        </div>
        <div className={css.inputActions} style={{ right: 0 }}>
          {actions}
        </div>
        {isDropdownOpen && (
          <div ref={refs.setFloating} style={floatingStyles}>
            <DropDown
              style={{ margin: 0 }}
              ref={dropdownRef}
              options={dropdownOptions}
              selectedIndex={0}
              displayAbove={!!displayAbove}
              handleChange={handleDropdownClick}
              handleClose={closeDropdown}
            />
          </div>
        )}
      </div>
    );
  }
);

ChatInputFieldSet.displayName = "ChatInputFieldSet";
